// Angular dependencies
import { Injectable } from '@angular/core';

import { ApiService } from '../core/upgraded-providers';
import { VersionService } from '../version.service';

import {
    tap,
    takeUntil,
    bufferTime,
    filter,
    switchMap,
    concatMap
} from 'rxjs/operators';

import {
    Subject,
    Observable,
    of,
    from
} from 'rxjs';

const MIN_ROOMS_QUERY = 600;
const MAX_ROOMS_QUERIES = 3;

@Injectable()
export class WorldMapService {
    _destroySbj: Subject<void> = new Subject();

    _querySbj: Subject<[string[], string]> = new Subject();
    _responseRoomsTerrainSbj: Subject<IRoom[]> = new Subject();
    responseRoomsTerrain$ = this._responseRoomsTerrainSbj.asObservable();

    constructor(
        private _apiService: ApiService,
        private _versionSrv: VersionService
    ) {
        this._querySbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(bufferTime(10))
            .pipe(filter((rooms) => !!rooms.length))
            .pipe(switchMap((requests) => this._execRoomsTerrains(requests)))
            .pipe(switchMap((responses) => from(responses).pipe(concatMap((response) => {
                return response.catch((query) => this._querySbj.next(query));
            }))))
            .pipe(filter((response) => !!response))
            .pipe(tap((response: IRoom[]) => this._responseRoomsTerrainSbj.next(response)))
            .subscribe();
    }

    getRoomsTerrains(rooms: string[], shard: string): Observable<any> {
        this._querySbj.next([rooms, shard]);

        return this.responseRoomsTerrain$;
    }

    _execRoomsTerrains(queries: [string[], string][]) {
        const shard = queries[0][1];

        const rooms = [];
        queries.forEach(([query]) => rooms.push(...query));

        const hash = getHash(this._apiService.options, this._versionSrv.data);
        const database = `screeps_${ shard }${ hash }`;

        let resolves: ((value?: IRoom[]) => void)[] = [];
        const rejects: ((value?: [string[], string]) => void)[] = [];
        const promises = queries.map(() => {
            return new Promise<IRoom[]>((resolve, reject) => {
                resolves.push(resolve);
                rejects.push(reject);
            });
        });

        setTimeout(async () => {
            const [roomsData] = await readRoomsFromDatabase(rooms, database);
            // Resolve matches queries
            queries = queries.filter(([query], index: number) => {
                const notFull = query.some((room) => !roomsData[room]);
                if (notFull) {
                    return true;
                }

                const response = query.map((room) => roomsData[room]);
                resolves[index](response);
                resolves[index] = null;
            });

            resolves = resolves.filter((r) => !!r);

            const subqueries = queries.map(([query]) => {
                return query ? query.filter((room) => !roomsData[room]) : [];
            });

            const length = subqueries.reduce((sum, query) => sum + query.length, 0);

            if (length) {
                const max = Math.max(MIN_ROOMS_QUERY, Math.floor(length / MAX_ROOMS_QUERIES));
                const sequences: string[][] = [[]];
                subqueries.reduce((sequence: string[], query: string[]) => {
                    sequence.push(...query);
                    if (sequence.length > max) {
                        sequence = [];
                        sequences.push(sequence);
                    }

                    return sequence;
                }, sequences[0]);

                const requests = sequences.map((rooms) => {
                    return new Promise<IRoom[]>(async (resolve) => {
                        try {
                            const response = await this._getRoomsTerrain(rooms, shard);
                            const mapRooms = response.reduce((acc, room) => ({ ...acc, [room.room]: room}), {});
                            rooms.forEach((room) => {
                                if (mapRooms[room]) {
                                    return;
                                }

                                mapRooms[room] = { room };
                            });

                            resolve(Object.values(mapRooms));
                        } catch (err) {
                            resolve(null);
                        }
                    });
                });

                const rooms: IRoom[] = await Promise.all(requests).then((responses) => {
                    return responses.filter((r) => !!r).reduce((data: IRoom[], rooms) => {
                        data.push(...rooms);
                        return data;
                    }, []);
                });

                addRoomsToDatabase(rooms, database);
                rooms.forEach((room) => roomsData[room.room] = room);

                queries.forEach(([query, shard], index) => {
                    const notFull = query.some((room) => !roomsData[room]);
                    if (notFull) {
                        rejects[index]([query, shard]);
                        return;
                    }

                    const response = query.map((room) => roomsData[room]);
                    resolves[index](response);
                });
            }
        });

        return of(promises);
    }

    destroy() {
        this._destroySbj.next();
        this._destroySbj.complete();
    }

    async getWorldSize(shard: string): Promise<{ width: number; height: number; }> {
        const url = `game/world-size?shard=${ shard }`;

        return await this._apiService.get(url);
    }

    async getRandomNoviceRoom(shard: string): Promise<any> {
        const url = `game/random-novice-room?shard=${ shard }`;

        try {
            const { shard, room } = await this._apiService.get(url);

            if (!shard || !room) {
                throw new Error('no room data');
            }

            return { shard, room };
        } catch (err) {
            throw err;
        }
    }

    async getStats(rooms, shard, statName = 'owner0'): Promise<IMapStats> {
        const url = `game/map-stats`;

        let data;
        try {
            data = await this._apiService.post(url, {
                rooms,
                shard,
                statName
            });
        } catch (err) {
            // Noop.
        }

        return data;
    }

    async _getRoomsTerrain(rooms, shard): Promise<IRoom[]> {
        const url = `game/rooms?encoded=true&shard=${ shard }`;

        let data;
        try {
            const response = await this._apiService.post(url, { rooms });
            data = response.rooms;
        } catch (err) {
            throw new Error(err);
        }

        return data;
    }
}

function readRoomsFromDatabase(rooms, database): Promise<[{
    [key: string]: IRoom
}, string[]]> {
    const readWorker = new Worker('./indexed-db-read.worker', { type: 'module' });

    return new Promise((resolve) => {
        readWorker.postMessage({ rooms, database });

        readWorker.onmessage = ({ data }) => {
            resolve(data);

            readWorker.terminate();
        };
    });
}

function addRoomsToDatabase(rooms, database): Promise<void> {
    const addWorker = new Worker('./indexed-db-add.worker', { type: 'module' });

    return new Promise((resolve) => {
        addWorker.postMessage({ rooms, database });

        addWorker.onmessage = () => {
            resolve();

            addWorker.terminate();
        };
    });
}

function getHash({ official, apiUrl }, { databaseVersion }: IVersionData): string {
    if (official) {
        return databaseVersion || '';
    }

    return '_' + hashCode(apiUrl);
}

function hashCode(str: string): string {
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        const chr = str.charCodeAt(i);

        // tslint:disable-next-line:no-bitwise
        hash  = ((hash << 5) - hash) + chr;
        // tslint:disable-next-line:no-bitwise
        hash |= 0; // Convert to 32bit integer
    }

    return hash.toString();
}
